import type { Vec3 } from "./entityData";
import { toNameFloatMap, vec3From, type NameFloatMap } from "./nameFloatMap";
import { IDENTITY_SET_POSE, type SelectionSetPose } from "./selectionSetTransform";

/**
 * `SelectionSetData` — web shape of Unity's `SelectionSetData`.
 *
 * Dynamo addressing mirrors `DynamoClient.GetSelectionSets`:
 *   pk = "{customerId}:project"
 *   sk between "$project:{projectId}:scene:{sceneId}:selectionset:{MinGuid}"
 *               and "…{MaxGuid}"
 */
export interface SelectionSetData {
  readonly ID: string;
  readonly Name: string;
  readonly DisplayOrder?: number;
  readonly EntitySnapshotIDs?: ReadonlyArray<string>;
  readonly MediaTileIDs?: ReadonlyArray<string>;
  readonly IsVisible?: boolean;
  readonly IsForcedVisible?: boolean;

  readonly Position?: NameFloatMap | unknown;
  readonly Rotation?: NameFloatMap | unknown;
  readonly Scale?: NameFloatMap | unknown;
  readonly Pivot?: NameFloatMap | unknown;
  readonly PivotR?: NameFloatMap | unknown;
  readonly PivotS?: NameFloatMap | unknown;
  readonly IntPosition?: NameFloatMap | unknown;
  readonly IntRotation?: NameFloatMap | unknown;
  readonly IntScale?: NameFloatMap | unknown;

  readonly [extra: string]: unknown;
}

export type SelectionSetUpdateFlag = "Name" | "Pose" | "Entities" | "Visibility";

export function sortSelectionSets(
  selectionSets: ReadonlyArray<SelectionSetData>,
): ReadonlyArray<SelectionSetData> {
  return [...selectionSets].sort((a, b) =>
    (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0) || a.Name.localeCompare(b.Name),
  );
}

export function nextSelectionSetName(
  selectionSets: ReadonlyArray<SelectionSetData>,
): string {
  let highestIndex = 1;
  for (const set of selectionSets) {
    const match = /^Set\s+(\d+)$/i.exec(set.Name.trim());
    if (!match) continue;
    highestIndex = Math.max(highestIndex, Number.parseInt(match[1], 10) + 1);
  }
  return `Set ${highestIndex}`;
}

/**
 * The set's CURRENT pose (`Position` / `Rotation` / `Scale`). Absent reads as
 * identity, which is what `createSelectionSetAsync` writes: a brand-new set has
 * not been moved, so its members' own transforms are the whole truth.
 */
export function selectionSetPose(selectionSet: SelectionSetData): SelectionSetPose {
  return {
    position: vec3From(toNameFloatMap(selectionSet.Position), IDENTITY_SET_POSE.position),
    rotation: vec3From(toNameFloatMap(selectionSet.Rotation), IDENTITY_SET_POSE.rotation),
    scale: vec3From(toNameFloatMap(selectionSet.Scale), IDENTITY_SET_POSE.scale),
  };
}

/**
 * The pose the set was created at (`IntPosition` / `IntRotation` / `IntScale`)
 * — what Placement's Reset returns it to. Named `Int*` upstream for "initial",
 * and seeded to identity by `createSelectionSetAsync`.
 */
export function selectionSetInitialPose(selectionSet: SelectionSetData): SelectionSetPose {
  return {
    position: vec3From(toNameFloatMap(selectionSet.IntPosition), IDENTITY_SET_POSE.position),
    rotation: vec3From(toNameFloatMap(selectionSet.IntRotation), IDENTITY_SET_POSE.rotation),
    scale: vec3From(toNameFloatMap(selectionSet.IntScale), IDENTITY_SET_POSE.scale),
  };
}

/**
 * The stored pivot, or `null` when the set has never been transformed. A null
 * pivot means "recompute from the members" — the pivot is only frozen onto the
 * row once a move has happened, so a set whose members were edited individually
 * still rotates about where they are NOW rather than where they once were.
 */
export function selectionSetPivotOverride(selectionSet: SelectionSetData): Vec3 | null {
  const map = toNameFloatMap(selectionSet.Pivot);
  if (Object.keys(map).length === 0) return null;
  return vec3From(map, [0, 0, 0]);
}

export function nextSelectionSetDisplayOrder(
  selectionSets: ReadonlyArray<SelectionSetData>,
): number {
  if (selectionSets.length === 0) return 0;
  return Math.max(...selectionSets.map((set) => set.DisplayOrder ?? 0)) + 1;
}
